import React, { useState } from 'react';
import { Plus, Calendar as CalendarIcon, Check, X, Trash2, Pencil } from 'lucide-react';
import { useCreateSchedule, useDeleteSchedule, useDeleteSharedSchedule, useUpdateSchedule } from '../../api/schedules';
import { useNotifications } from '../../context/NotificationContext';
import { useCampContext } from '../../context/CampContext';
import type { ScheduleItem } from '../../api/schedules';
import './CalendarsList.css';

export const CALENDAR_COLORS = [
  '#4ECDC4', // Teal
  '#F7DC6F', // Gold
  '#BB8FCE', // Lavender
  '#45B7D1', // Sky blue
  '#96CEB4', // Sage green
  '#FFEAA7', // Soft yellow
  '#DDA0DD', // Plum
  '#98D8C8', // Mint
  '#85C1E9', // Light blue
];

export const getCalendarColor = (index: number): string => {
  return CALENDAR_COLORS[index % CALENDAR_COLORS.length];
};

interface CalendarsListProps {
  schedules: ScheduleItem[];
  allowAddingAndEditing?: boolean;
  colorIndexOffset?: number;
}

const CalendarsList: React.FC<CalendarsListProps> = ({
  schedules,
  allowAddingAndEditing = true,
  colorIndexOffset = 0,
}) => {
  const {
    selectedScheduleIds,
    currentScheduleUuid,
    setCurrentScheduleUuid,
    handleScheduleToggle,
    handleScheduleDelete
  } = useCampContext();
  const [isAddingCalendar, setIsAddingCalendar] = useState(false);
  const [newCalendarName, setNewCalendarName] = useState('');
  const [deleteConfirmSchedule, setDeleteConfirmSchedule] = useState<ScheduleItem | null>(null);
  const [editingScheduleUuid, setEditingScheduleUuid] = useState<string | null>(null);
  const [editedName, setEditedName] = useState('');
  const createScheduleMutation = useCreateSchedule();
  const updateScheduleMutation = useUpdateSchedule();
  const deleteScheduleMutation = useDeleteSchedule();
  const deleteSharedScheduleMutation = useDeleteSharedSchedule();
  const { showSuccess, showError } = useNotifications();

  const handleDeleteClick = (e: React.MouseEvent, schedule: ScheduleItem) => {
    e.stopPropagation();
    setDeleteConfirmSchedule(schedule);
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmSchedule) return;

    // Check if it's a shared calendar (has share_uuid)
    if (deleteConfirmSchedule.share_uuid) {
      deleteSharedScheduleMutation.mutate(
        { scheduleUuid: deleteConfirmSchedule.uuid, shareUuid: deleteConfirmSchedule.share_uuid },
        {
          onSuccess: () => {
            showSuccess(`Shared calendar "${deleteConfirmSchedule.name}" removed`);
            handleScheduleDelete(deleteConfirmSchedule.uuid);
            setDeleteConfirmSchedule(null);
          },
          onError: (error) => {
            showError('Failed to remove shared calendar', error);
          },
        }
      );
    } else {
      // Regular calendar deletion
      deleteScheduleMutation.mutate(deleteConfirmSchedule.uuid, {
        onSuccess: () => {
          showSuccess(`Calendar "${deleteConfirmSchedule.name}" deleted`);
          handleScheduleDelete(deleteConfirmSchedule.uuid);
          setDeleteConfirmSchedule(null);
        },
        onError: (error) => {
          showError('Failed to delete calendar', error);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmSchedule(null);
  };

  const handleAddCalendar = () => {
    setIsAddingCalendar(true);
    setNewCalendarName('');
  };

  const handleCancelAddCalendar = () => {
    setIsAddingCalendar(false);
    setNewCalendarName('');
  };

  const handleSaveCalendar = async () => {
    if (!newCalendarName.trim()) {
      showError('Please enter a calendar name', new Error('Calendar name is required'));
      return;
    }

    createScheduleMutation.mutate(
      { name: newCalendarName.trim() },
      {
        onSuccess: (data) => {
          showSuccess(`Calendar "${data.name}" created successfully`);
          setIsAddingCalendar(false);
          setNewCalendarName('');
          handleScheduleToggle(data.uuid);
          setCurrentScheduleUuid(data.uuid);
        },
        onError: (error) => {
          showError('Failed to create calendar', error);
        },
      }
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveCalendar();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelAddCalendar();
    }
  };

  const handleEditClick = (e: React.MouseEvent, schedule: ScheduleItem) => {
    e.stopPropagation();
    setEditingScheduleUuid(schedule.uuid);
    setEditedName(schedule.name);
  };

  const handleCancelEdit = () => {
    setEditingScheduleUuid(null);
    setEditedName('');
  };

  const handleSaveEdit = () => {
    if (!editingScheduleUuid || !editedName.trim()) {
      showError('Please enter a calendar name', new Error('Calendar name is required'));
      return;
    }

    updateScheduleMutation.mutate(
      { scheduleUuid: editingScheduleUuid, name: editedName.trim() },
      {
        onSuccess: () => {
          showSuccess('Calendar name updated successfully');
          setEditingScheduleUuid(null);
          setEditedName('');
        },
        onError: (error) => {
          showError('Failed to update calendar name', error);
        },
      }
    );
  };

  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  return (
    <>
      <div className="calendars-list">
        {schedules && schedules.length > 0 ? (
          schedules.map((schedule, index) => {
            const isEditing = editingScheduleUuid === schedule.uuid;
            return (
              <div key={schedule.uuid}>
                {isEditing ? (
                  <div className="edit-calendar-form">
                    <input
                      type="text"
                      className="edit-calendar-input"
                      placeholder="Calendar name"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={handleEditKeyPress}
                      autoFocus
                      maxLength={50}
                    />
                    <div className="edit-calendar-actions">
                      <button
                        className="btn btn--primary btn--sm"
                        onClick={handleSaveEdit}
                        disabled={!editedName.trim() || updateScheduleMutation.isPending}
                      >
                        <Check className="icon icon--xs" />
                        Save
                      </button>
                      <button
                        className="btn btn--secondary btn--sm"
                        onClick={handleCancelEdit}
                        disabled={updateScheduleMutation.isPending}
                      >
                        <X className="icon icon--xs" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`calendar-item ${schedule.uuid === currentScheduleUuid ? 'calendar-item--active' : ''}`}
                    onClick={() => setCurrentScheduleUuid(schedule.uuid)}
                  >
                    <input
                      type="checkbox"
                      className="calendar-item-checkbox"
                      checked={selectedScheduleIds.includes(schedule.uuid)}
                      onChange={() => handleScheduleToggle(schedule.uuid)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span
                      className="calendar-item-color"
                      style={{ backgroundColor: CALENDAR_COLORS[(index + colorIndexOffset) % CALENDAR_COLORS.length] }}
                    />
                    <CalendarIcon className="calendar-item-icon" />
                    <span className="calendar-item-name">{schedule.name}</span>
                    {allowAddingAndEditing && (
                      <button
                        className="calendar-item-edit"
                        onClick={(e) => handleEditClick(e, schedule)}
                        aria-label="Edit calendar name"
                      >
                        <Pencil className="icon icon--sm" />
                      </button>
                    )}
                    <button
                      className="calendar-item-delete"
                      onClick={(e) => handleDeleteClick(e, schedule)}
                      aria-label="Delete calendar"
                    >
                      <Trash2 className="icon icon--sm" />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="schedule-sidebar-message">No calendars yet.</div>
        )}
      </div>

      {allowAddingAndEditing && (
        isAddingCalendar ? (
          <div className="add-calendar-form">
            <input
              type="text"
              className="add-calendar-input"
              placeholder="Calendar name"
              value={newCalendarName}
              onChange={(e) => setNewCalendarName(e.target.value)}
              onKeyDown={handleKeyPress}
              autoFocus
              maxLength={50}
            />
            <div className="add-calendar-actions">
              <button
                className="btn btn--primary btn--sm"
                onClick={handleSaveCalendar}
                disabled={!newCalendarName.trim() || createScheduleMutation.isPending}
              >
                <Check className="icon icon--xs" />
                Save
              </button>
              <button
                className="btn btn--secondary btn--sm"
                onClick={handleCancelAddCalendar}
                disabled={createScheduleMutation.isPending}
              >
                <X className="icon icon--xs" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="btn btn--secondary btn--sm add-calendar-btn" onClick={handleAddCalendar}>
            <Plus className="icon icon--xs" />
            Add Calendar
          </button>
        )
      )}

      {deleteConfirmSchedule && (
        <div className="delete-confirm-overlay" onClick={handleCancelDelete}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="delete-confirm-title">
              {deleteConfirmSchedule.share_uuid ? 'Remove Shared Calendar?' : 'Delete Calendar?'}
            </h3>
            <p className="delete-confirm-message">
              {deleteConfirmSchedule.share_uuid
                ? `Are you sure you want to remove "${deleteConfirmSchedule.name}"? This will remove the shared calendar from your account, but won't affect the original calendar.`
                : `Are you sure you want to delete "${deleteConfirmSchedule.name}"? This will permanently delete the calendar and any events saved to it.`
              }
            </p>
            <div className="delete-confirm-actions">
              <button
                className="btn btn--secondary btn--sm"
                onClick={handleCancelDelete}
                disabled={deleteScheduleMutation.isPending || deleteSharedScheduleMutation.isPending}
              >
                Cancel
              </button>
              <button
                className="btn btn--primary btn--sm"
                onClick={handleConfirmDelete}
                disabled={deleteScheduleMutation.isPending || deleteSharedScheduleMutation.isPending}
              >
                {(deleteScheduleMutation.isPending || deleteSharedScheduleMutation.isPending)
                  ? (deleteConfirmSchedule.share_uuid ? 'Removing...' : 'Deleting...')
                  : (deleteConfirmSchedule.share_uuid ? 'Remove' : 'Delete')
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarsList;
